import { useQuery } from '@tanstack/react-query';
import { airportService } from '@/services/airportService';
import { queryClient } from '@/lib/queryClient.js';

/**
 * Query key for /config/airports. utils/airports.js reads the cached list via
 * getCachedAirports() below.
 */
export const AIRPORTS_QUERY_KEY = ['airports'];

/**
 * Shared query options for /config/airports — used by the useAirports() hook
 * (app root + the booking-modal gate) and by ensureAirports() below. Same
 * queryKey, so they all share one cache entry.
 *
 * Static reference data: cached for the whole session and never garbage
 * collected. A full page reload clears the in-memory cache, so the list is
 * re-fetched once per load.
 */
export const airportsQueryOptions = {
    queryKey: AIRPORTS_QUERY_KEY,
    queryFn: async () => {
        const result = await airportService.getAirports();
        if (!result.success) throw new Error(result.error);
        return result.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
};

export function useAirports({enabled = true} = {}) {
    return useQuery({...airportsQueryOptions, enabled});
}

/**
 * Synchronous read of the cached airport list for non-React callers (trip
 * direction detection, booking/update payload builders). Returns [] until the
 * query lands — which is why it is fetched at app root (AppConfigLoader) for a
 * signed-in session, before any route can run detection. Signed out it is not
 * prefetched: the surfaces that run detection (hopnModal's boot gate,
 * useAirportDetector) call this hook themselves. See utils/airports.js.
 */
export function getCachedAirports() {
    const airports = queryClient.getQueryData(AIRPORTS_QUERY_KEY);
    return Array.isArray(airports) ? airports : [];
}

/**
 * Imperative one-shot fetch for callers that must not run airport DETECTION
 * until the list is in cache — detection is synchronous and reports "not an
 * airport" against an empty cache, which would mean the wrong trip_direction
 * (and so the wrong pricing and the wrong flow) for a real airport trip.
 *
 * Resolves from cache when already loaded and dedupes against an in-flight
 * request, so on the normal path this is free. Swallows the error: it stays on
 * the query, so a failed fetch surfaces on the modal gate's
 * "unavailable + Retry" branch.
 */
export async function ensureAirports() {
    try {
        return await queryClient.fetchQuery(airportsQueryOptions);
    } catch {
        return null;
    }
}
